import { MoveLeft, MoveRight } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Page number generation — shows at most 7 slots with ellipsis
// ---------------------------------------------------------------------------

type PageItem = number | "…";

function getPageItems(current: number, total: number): PageItem[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items: PageItem[] = [1];

  if (current > 3) items.push("…");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p++) items.push(p);

  if (current < total - 2) items.push("…");

  items.push(total);

  return items;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WorkingTablePaginationProps {
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkingTablePagination({
  page,
  pageCount,
  onPage,
}: WorkingTablePaginationProps) {
  const { t } = useLingui();
  const items = getPageItems(page, pageCount);
  const canPrev = page > 1;
  const canNext = page < pageCount;

  return (
    <div className="flex items-center justify-between px-3 py-2">
      {/* Previous */}
      <Button
        variant="outline"
        disabled={!canPrev}
        onClick={() => onPage(page - 1)}
        className="h-11 w-30 gap-2 rounded-xl border-(--color-border) bg-(--color-surface-content) text-sm font-medium text-(--color-text-primary) hover:bg-(--color-surface-muted)"
      >
        <MoveLeft className="h-5 w-5 shrink-0" />
        {t`Previous`}
      </Button>

      {/* Page numbers */}
      <div className="flex items-center gap-0.5">
        {items.map((item, i) =>
          item === "…" ? (
            <span
              key={`ellipsis-${i}`}
              className="flex h-10 w-10 items-center justify-center text-sm font-medium text-(--color-text-secondary) select-none"
            >
              …
            </span>
          ) : (
            <Button
              key={item}
              variant="ghost"
              size="icon"
              onClick={() => onPage(item)}
              disabled={item === page}
              aria-current={item === page ? "page" : undefined}
              className={cn(
                "rounded-lg text-sm font-medium",
                item === page
                  ? "bg-(--color-surface-muted) text-(--color-text-primary) cursor-default disabled:opacity-100"
                  : "text-(--color-text-secondary) hover:bg-(--color-surface-muted)",
              )}
            >
              {item}
            </Button>
          ),
        )}
      </div>

      {/* Next */}
      <Button
        variant="outline"
        disabled={!canNext}
        onClick={() => onPage(page + 1)}
        className="h-11 w-30 gap-2 rounded-xl border-(--color-border) bg-(--color-surface-content) text-sm font-medium text-(--color-text-primary) hover:bg-(--color-surface-muted)"
      >
        {t`Next`}
        <MoveRight className="h-5 w-5 shrink-0" />
      </Button>
    </div>
  );
}
